import { Trans } from "@lingui/react/macro";
import { deducedError } from "@/Utils/ErrorUtils";
import {
  activityRequirementSchema,
  type IActivityRequirement,
} from "@medoma/models/activityRequirements";
import { compareShiftsForAssignList, useScheduledShifts } from "@/api/Shifts";
import { useCurrentEmployee } from "@/api/Employee";
import DropdownMenu from "@/components/DropdownMenu/DropdownMenu";
import NoResults from "@/components/NoResults/NoResults";
import TeamBadge from "@/components/Teams/TeamBadge";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Loading } from "@medoma/components/Loading/Loading";
import AddPersonIcon from "@medoma/components/icons/AddPersonIcon";
import type {
  IMedicalCompetence,
  IScheduledShift,
} from "@medoma/models/shifts";
import type { ITeam } from "@medoma/models/patients";
import {
  calculateAvailableShifts,
  medicalCompetenceDictionary,
  shiftName,
  shiftTimeDisplayValues,
} from "@medoma/models/shifts";
import styles from "./ShiftDropdownMenu.module.scss";
import * as Sentry from "@sentry/react";
import { formattedTimeSpan } from "@/components/Time/timeUtils";
import { Text } from "@medoma/components/Text/Text";
import { useLingui } from "@lingui/react/macro";
import type { IBaseButton } from "@medoma/components/Button/Button";

const Shift = ({
  employee,
  competence,
  startDateTime,
  endDateTime,
  team,
  selectedDate,
}: Pick<
  IScheduledShift,
  "employee" | "competence" | "startDateTime" | "endDateTime" | "team"
> & { selectedDate: Date }) => {
  const { t } = useLingui();
  const {
    startedDayBefore,
    hasEndTimeAfterToday,
    formattedDayBefore,
    formattedDayToday,
  } = shiftTimeDisplayValues({
    selectedDate,
    startDateTime,
    endDateTime,
  });

  const additionalContext = `${startedDayBefore ? t`Start ${formattedDayBefore}` : hasEndTimeAfterToday ? t`Start ${formattedDayToday}` : ""}`;

  return (
    <>
      <span className={styles.shift}>
        <span className={styles.nameAndTeam}>
          <Text element="p" size="base">{`${shiftName(
            {
              employee,
              competence,
            },
            { competenceFormat: "full", employeeNameFormat: "full" },
          )}`}</Text>
          {team ? <TeamBadge team={team} size="small" /> : null}
        </span>
        <Text element="p" color="faded" size="small">
          {`${additionalContext}${additionalContext ? " " : ""}${formattedTimeSpan(startDateTime, endDateTime)} `}
        </Text>
      </span>
    </>
  );
};

const ErrorDropdownItem = ({ error }: { error: Error }) => {
  const { t } = useLingui();
  Sentry.captureException(error);
  return (
    <DropdownMenu.Item
      content={
        <ErrorMessage
          message={`${t`Kunde inte hämta arbetspassen.`} ${deducedError(error)}`}
        />
      }
    />
  );
};

export const ShiftDropdownMenu = ({
  assignedShiftIds,
  unfulfilledRequirements,
  onAllocate,
  isAllocating = false,
  shiftsFromDate,
  shiftsUntilDate = shiftsFromDate,
  size = "medium",
  triggerType = "icon",
  patientTeam,
}: {
  assignedShiftIds: number[];
  unfulfilledRequirements: IActivityRequirement[];
  onAllocate: (shiftIdToAdd: number) => void;
  isAllocating?: boolean;
  shiftsFromDate: Date;
  shiftsUntilDate?: Date;
  size?: IBaseButton["size"];
  triggerType?: "icon" | "icon-and-text";
  patientTeam: ITeam | null | undefined;
}) => {
  const { t } = useLingui();
  const {
    data: scheduledShifts,
    isPending,
    isError,
    error,
  } = useScheduledShifts(shiftsFromDate, shiftsUntilDate);
  const { data: currentEmployee } = useCurrentEmployee();

  const availableShifts = calculateAvailableShifts({
    scheduledShifts: scheduledShifts ?? [],
    assignedShiftIds,
  }).sort(
    compareShiftsForAssignList({
      currentEmployeeId: currentEmployee?.id,
      patientTeam,
    }),
  );

  const currentUserShifts = currentEmployee
    ? availableShifts.filter(
        ({ employee }) => employee?.id === currentEmployee.id,
      )
    : [];
  const otherEmployeesShifts = currentEmployee
    ? availableShifts.filter(
        ({ employee }) => employee?.id !== currentEmployee.id,
      )
    : availableShifts;

  const unfulfilledMedicalRequirements = unfulfilledRequirements.filter(
    (unfulfilledRequirement) =>
      unfulfilledRequirement !== activityRequirementSchema.enum.DoubleStaffing,
  );

  const otherShifts = otherEmployeesShifts.filter(
    ({ competence }) => !unfulfilledRequirements.includes(competence),
  );

  return (
    <DropdownMenu
      trigger={{
        icon: <AddPersonIcon />,
        ...(triggerType === "icon-and-text"
          ? { text: t`Lägg till` }
          : { ariaLabel: t`Lägg till personal` }),
        disabled: isAllocating,
        size: size,
      }}
    >
      {isPending ? (
        <DropdownMenu.Item
          content={<Loading message={t`Laddar arbetspass`} />}
        />
      ) : isError ? (
        <ErrorDropdownItem error={error} />
      ) : availableShifts.length === 0 ? (
        <DropdownMenu.Item
          content={<NoResults message={t`Ingen tillgänglig personal`} />}
        />
      ) : (
        <>
          {currentUserShifts.length >= 1 ? (
            <>
              <DropdownMenu.Group>
                <>
                  {currentUserShifts.map(
                    ({
                      id,
                      employee,
                      startDateTime,
                      endDateTime,
                      competence,
                      team,
                    }) => (
                      <DropdownMenu.Item
                        action={() => {
                          onAllocate(id);
                        }}
                        content={
                          <Shift
                            competence={competence}
                            endDateTime={endDateTime}
                            startDateTime={startDateTime}
                            employee={employee}
                            team={team}
                            selectedDate={shiftsFromDate}
                          />
                        }
                        key={id}
                      />
                    ),
                  )}
                </>
              </DropdownMenu.Group>
              {unfulfilledMedicalRequirements.length >= 1 ||
              otherShifts.length >= 1 ? (
                <DropdownMenu.Separator />
              ) : null}
            </>
          ) : null}
          {unfulfilledMedicalRequirements.map(
            (unfulfilledRequirement, index) => {
              const shiftsMatchingUnfulfilledMedicalRequirement =
                otherEmployeesShifts.filter(
                  ({ competence }) => competence === unfulfilledRequirement,
                );
              return (
                <DropdownMenu.Group key={unfulfilledRequirement}>
                  <DropdownMenu.Label>
                    {`${
                      // `filter` ensures `as` is safe
                      t(
                        medicalCompetenceDictionary[
                          unfulfilledRequirement as IMedicalCompetence
                        ].long,
                      )
                    } (${shiftsMatchingUnfulfilledMedicalRequirement.length})`}
                  </DropdownMenu.Label>
                  <>
                    {shiftsMatchingUnfulfilledMedicalRequirement.map(
                      ({
                        id,
                        employee,
                        startDateTime,
                        endDateTime,
                        competence,
                        team,
                      }) => {
                        return (
                          <DropdownMenu.Item
                            action={() => {
                              onAllocate(id);
                            }}
                            content={
                              <Shift
                                competence={competence}
                                endDateTime={endDateTime}
                                startDateTime={startDateTime}
                                employee={employee}
                                team={team}
                                selectedDate={shiftsFromDate}
                              />
                            }
                            key={id}
                          />
                        );
                      },
                    )}
                  </>
                  {index !== unfulfilledMedicalRequirements.length - 1 ||
                  otherShifts.length >= 1 ? (
                    <DropdownMenu.Separator />
                  ) : (
                    <></>
                  )}
                </DropdownMenu.Group>
              );
            },
          )}
          {otherShifts.length >= 1 ? (
            <DropdownMenu.Group>
              <>
                <DropdownMenu.Label>
                  <Trans>Övriga</Trans>
                </DropdownMenu.Label>
                {otherShifts.map(
                  ({
                    id,
                    employee,
                    startDateTime,
                    endDateTime,
                    competence,
                    team,
                  }) => (
                    <DropdownMenu.Item
                      action={() => {
                        onAllocate(id);
                      }}
                      content={
                        <Shift
                          competence={competence}
                          endDateTime={endDateTime}
                          startDateTime={startDateTime}
                          employee={employee}
                          team={team}
                          selectedDate={shiftsFromDate}
                        />
                      }
                      key={id}
                    />
                  ),
                )}
              </>
            </DropdownMenu.Group>
          ) : (
            <></>
          )}
        </>
      )}
    </DropdownMenu>
  );
};
